import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {Component, Inject, OnDestroy, OnInit, Type, ViewEncapsulation} from "@angular/core";
import {ModelName} from '../../_typing/generic-types';
import {GenericChartTileConfiguration} from '../chart-config/generic-chart-tile.configuration';
import {ChartDialogService} from './chart-dialog.service';
import {HeaderDataService} from '../../services/header_data.service';
import {Series} from '../../_models/series';
import {first, takeUntil} from 'rxjs/operators';
import {ApiService} from "../../services/api/api.service";
import {IDateTimePeriod} from "../../_typing/date-time-period";
import {CdkDrag, CdkDragHandle} from "@angular/cdk/drag-drop";
import { CommonModule, NgComponentOutlet } from "@angular/common";
import {COMMON_DIALOG_IMPORTS} from "../../shared/import-groups/Common";
import {AlertsTableComponent} from "../../tables/alerts-table/alerts-table.component";
import {SeriesAuditHistoryComponent} from "../../components/series-audit-history/series-audit-history.component";
import {NameAndDescriptionPipe} from "../../shared/pipes";
import {CustomChartComponent} from "./custom-chart/custom-chart.component";
import {DateTimePeriodComponent} from "../../date-time/date-time-period.component";
import {deepCopy} from "../../lib/utils";
import {DateTimeInstanceService} from "../../services/date-time-instance.service";
import {Subject} from "rxjs";

export type ChartDialogConfig = {
    series_name?: ModelName,
    dtp?: IDateTimePeriod
} & GenericChartTileConfiguration;

export interface DialogData {
    form_result: string;
    chart_component?: string;
    config?: ChartDialogConfig;
}

@Component({
    selector: 'chart-dialog',
    templateUrl: 'chart-dialog.component.html',
    providers: [DateTimeInstanceService, ChartDialogService],
    imports: [
        CdkDrag,
        CdkDragHandle,
        COMMON_DIALOG_IMPORTS,
        CommonModule,
        AlertsTableComponent,
        SeriesAuditHistoryComponent,
        NameAndDescriptionPipe,
        CustomChartComponent,
        DateTimePeriodComponent,
        NgComponentOutlet
    ],
    encapsulation: ViewEncapsulation.None
})
export class ChartDialog implements OnInit, OnDestroy {
    private onDestroy: Subject<void> = new Subject();
    series_names: any;
    series_list: string[];
    series: Series;
    chart_component: string = '';
    config: ChartDialogConfig;
    dtp: IDateTimePeriod;
    current_tab: number = 0;
    data_ready = false;

    analysisTools: Type<unknown> | null = null;
    analysisToolsInputs: { selected_series: Series } = {selected_series: null};

    constructor(public dialogRef: MatDialogRef<ChartDialog>,
                @Inject(MAT_DIALOG_DATA) public data: DialogData,
                public headerData: HeaderDataService,
                public dateInst: DateTimeInstanceService,
                private api: ApiService) {

        this.config = this.data.config;
        if (this.data.chart_component && this.data.chart_component === 'custom-chart') {
            /**Used by flowcharts**/
            this.chart_component = this.data.chart_component;
            this.series_names = this.data.config.series_list.map(s => s.name);
            this.data_ready = true;
        } else if (this.data.config?.series_name) {
            this.series_names = [this.data.config.series_name];
        }

        this.dtp = deepCopy(this.dateInst.dtp);
        if (this.config.dtp) {
            this.dateInst.dtp = deepCopy(this.config.dtp);
            this.dateInst.emitDateTimePeriodChanged(this.dateInst.dtp);
        }

        if (this.series_names?.length === 1) {
            this.api.series.getByName(this.series_names[0]).pipe(
                first()
            ).subscribe(s => {
                this.series = s.data;
                this.analysisToolsInputs = {selected_series: this.series};
            });
        }
    }

    async ngOnInit() {
        const mod = await import('../../reports/analysis-tools/analysis-tools.component');
        this.analysisTools = mod.AnalysisToolsComponent;

        this.dateInst.dateTimePeriodChanged$.pipe(takeUntil(this.onDestroy)).subscribe((dtp) => {
            this.dtp = dtp;
        });
    }

    onCloseClick(): void {
        this.dialogRef.close();
    }

    ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.complete();
    }
}
